import React, { useEffect, useState } from "react";
import { addRecentProject } from "../../../services/recentProjects";
import { getCurrentProjectPath } from "../../../services/currentProject";
import { setCurrentSessionProject } from "../../../services/currentProject";
import { useSettingsStore } from "../../../store/useSettingsStore";
import { useModelStore } from "@/store/useModelStore";
import { saveDirectoryHandle } from "../../../services/fileHandleStorage";
import { exportToTerminalFormat } from "../../../services/terminalFormatter";

interface SaveProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: "save" | "saveAs";
  onSaveComplete?: () => void;
}

export const SaveProjectDialog: React.FC<SaveProjectDialogProps> = ({
  isOpen,
  onClose,
  mode = "saveAs",
  onSaveComplete,
}) => {
  const [projectName, setProjectName] = useState("");
  const [projectLocation, setProjectLocation] = useState("");
  const [directoryHandle, setDirectoryHandle] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [currentProjectPath, setCurrentProjectPath] = useState<string | null>(
    null
  );

  const settingsStore = useSettingsStore();
  const isElectron = typeof window !== "undefined" && window.electronAPI;

  const supportsFileSystemAccess =
    "showDirectoryPicker" in window && !isElectron;

  useEffect(() => {
    if (isOpen) {
      loadCurrentProject();
    }
  }, [isOpen, mode]);

  const loadCurrentProject = async () => {
    const path = await getCurrentProjectPath();
    setCurrentProjectPath(path);

    if (path) {
      if (isElectron) {
        const pathParts = path.split(/[\\/]/);
        const name = pathParts.pop() || "";
        const location = pathParts.join(isElectron ? "\\" : "/");
        setProjectName(name);
        setProjectLocation(location);
      } else if (!isElectron) {
        setProjectName(path.split("/").pop() || "");
        setProjectLocation(path);
      }
    } else {
      setProjectName("");
      setProjectLocation("");
    }
  };

  if (!isOpen) return null;

  const handleBrowse = async () => {
    setError("");

    if (isElectron) {
      try {
        const result = await window.electronAPI.openFile({
          properties: ["openDirectory", "createDirectory"],
          title: "Select Project Folder",
        });

        if (!result.canceled && result.filePaths && result.filePaths[0]) {
          const selectedPath = result.filePaths[0];

          const systemFolders = [
            "C:\\Windows",
            "C:\\Program Files",
            "C:\\Program Files (x86)",
            "C:\\System",
            "/System",
            "/Library",
            "/usr",
            "/bin",
            "/sbin",
          ];

          const isSystemFolder = systemFolders.some((sysPath) =>
            selectedPath.toLowerCase().startsWith(sysPath.toLowerCase())
          );

          if (isSystemFolder) {
            setError(
              "Cannot save to system folders. Please choose Documents, Desktop, or another user folder."
            );
            return;
          }

          setProjectLocation(selectedPath);
        }
      } catch (err: any) {
        if (err.message?.includes("system")) {
          setError(
            "Cannot access system folders. Please choose Documents, Desktop, or another location."
          );
        } else if (err.message?.includes("permission")) {
          setError(
            "Permission denied. Please choose a folder you have write access to."
          );
        } else {
          setError("Failed to select folder. Please try a different location.");
        }
      }
    } else if (supportsFileSystemAccess) {
      try {
        const dirHandle = await (window as any).showDirectoryPicker({
          mode: "readwrite",
        });
        setDirectoryHandle(dirHandle);
        setProjectLocation(dirHandle.name);
      } catch (err: any) {
        if (err.name !== "AbortError") {
          setError("Failed to select folder");
        }
      }
    } else {
      setError(
        "Your browser doesn't support directory selection. Files will be downloaded instead."
      );
    }
  };

  const handleSave = async () => {
    setError("");

    if (mode === "save") {
      if (!currentProjectPath) {
        setError("No project loaded. Please use Save As.");
        return;
      }
      await handleElectronSave();
      return;
    }

    if (!projectName.trim()) {
      setError("Project name is required");
      return;
    }

    if (isElectron) {
      if (!projectLocation) {
        setError("Please select a location");
        return;
      }
      await handleElectronSave();
    } else if (supportsFileSystemAccess && directoryHandle) {
      await handleBrowserSaveWithFS();
    } else {
      await handleBrowserSaveLegacy();
    }
  };

  // TERMINAL FORMAT - Returns text format, not JSON
  const collectCompleteData = () => {
    const terminalData = exportToTerminalFormat();
    return terminalData;
  };

  const handleElectronSave = async () => {
    setIsSaving(true);

    try {
      const terminalData = collectCompleteData();

      // Config stays as JSON (for settings only)
      const configData = {
        units: settingsStore.units,
        display: settingsStore.display,
        analysis: settingsStore.analysis,
        performance: settingsStore.performance,
      };

      const platform = await window.electronAPI.getPlatform();
      const separator = platform === "win32" ? "\\" : "/";

      const projectFolder =
        mode === "save" && currentProjectPath
          ? currentProjectPath
          : `${projectLocation}${separator}${projectName}`;

      const dataPath = `${projectFolder}${separator}data.fema`;
      const configPath = `${projectFolder}${separator}config.fema`;

      // Save data.fema as TERMINAL FORMAT TEXT
      const dataResult = await window.electronAPI.saveProjectDirect({
        filePath: dataPath,
        content: terminalData,
      });

      if (!dataResult.success) {
        throw new Error(dataResult.error || "Failed to save data.fema");
      }

      // Save config.fema as JSON (settings only)
      const configResult = await window.electronAPI.saveProjectDirect({
        filePath: configPath,
        content: JSON.stringify(configData, null, 2),
      });

      if (!configResult.success) {
        throw new Error(configResult.error || "Failed to save config.fema");
      }

      setCurrentSessionProject(projectFolder);

      try {
        const finalProjectName =
          projectFolder.split(/[\\/]/).pop() || projectName;
        await addRecentProject(finalProjectName, projectFolder);
        useModelStore.getState().setProjectName(finalProjectName);
      } catch (recentErr) {
        console.error("Exception adding to recent projects:", recentErr);
      }

      setSuccess(true);

      if (onSaveComplete) {
        onSaveComplete();
      }

      setTimeout(() => {
        setIsSaving(false);
        setSuccess(false);
        onClose();
        setProjectName("");
        setProjectLocation("");
      }, 1500);
    } catch (err: any) {
      console.error("SAVE ERROR:", err);
      setError(err.message || "Failed to save project");
      setIsSaving(false);
    }
  };

  const handleBrowserSaveWithFS = async () => {
    setIsSaving(true);

    try {
      // Get TERMINAL format data
      const terminalData = collectCompleteData();

      const configData = {
        units: settingsStore.units,
        display: settingsStore.display,
        analysis: settingsStore.analysis,
        performance: settingsStore.performance,
      };

      const projectFolderHandle = await directoryHandle.getDirectoryHandle(
        projectName,
        { create: true }
      );

      // Save data.fema as TERMINAL FORMAT TEXT
      const dataFileHandle = await projectFolderHandle.getFileHandle(
        "data.fema",
        { create: true }
      );
      const dataWritable = await dataFileHandle.createWritable();
      await dataWritable.write(terminalData); // Pure text
      await dataWritable.close();

      // Save config.fema
      const configFileHandle = await projectFolderHandle.getFileHandle(
        "config.fema",
        { create: true }
      );
      const configWritable = await configFileHandle.createWritable();
      await configWritable.write(JSON.stringify(configData, null, 2));
      await configWritable.close();

      const projectPath = `${directoryHandle.name}/${projectName}`;

      await saveDirectoryHandle(projectPath, projectFolderHandle);
      setCurrentSessionProject(projectPath);

      try {
        await addRecentProject(projectName, projectPath);
      } catch (err) {
        console.warn("Failed to add to recent projects:", err);
      }

      useModelStore.getState().setProjectName(projectName);

      setSuccess(true);

      if (onSaveComplete) {
        onSaveComplete();
      }

      setTimeout(() => {
        setIsSaving(false);
        setSuccess(false);
        onClose();
        setProjectName("");
        setProjectLocation("");
        setDirectoryHandle(null);
      }, 1500);
    } catch (err: any) {
      setError(err.message || "Failed to save project");
      setIsSaving(false);
    }
  };

  const handleBrowserSaveLegacy = async () => {
    setIsSaving(true);

    try {
      // Get TERMINAL format data
      const terminalData = collectCompleteData();

      const configData = {
        units: settingsStore.units,
        display: settingsStore.display,
        analysis: settingsStore.analysis,
        performance: settingsStore.performance,
      };

      // Download data.fema as TERMINAL FORMAT TEXT
      downloadFile(`${projectName}_data.fema`, terminalData);

      setTimeout(() => {
        downloadFile(
          `${projectName}_config.fema`,
          JSON.stringify(configData, null, 2)
        );
      }, 100);

      setSuccess(true);

      if (onSaveComplete) {
        onSaveComplete();
      }

      setTimeout(() => {
        setIsSaving(false);
        setSuccess(false);
        onClose();
        setProjectName("");
      }, 1500);
    } catch (err: any) {
      setError(err.message || "Failed to download files");
      setIsSaving(false);
    }
  };

  const downloadFile = (filename: string, content: string) => {
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCancel = () => {
    setProjectName("");
    setProjectLocation("");
    setDirectoryHandle(null);
    setError("");
    setSuccess(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg shadow-xl w-[520px] max-w-[90vw] border border-gray-700">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">
            {mode === "save" ? "Save Project" : "Save Project As"}
          </h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-4">
          {!isElectron && !supportsFileSystemAccess && (
            <div className="p-3 bg-yellow-900/30 border border-yellow-500 rounded text-yellow-300 text-sm">
              ⚠️ Your browser doesn't support folder selection. Files will be
              downloaded separately. Use Chrome/Edge 86+ for better experience.
            </div>
          )}

          {mode === "save" && currentProjectPath && (
            <div className="p-3 bg-blue-900/30 border border-blue-500 rounded text-blue-300 text-sm">
              ℹ️ Saving to: <br />
              <span className="font-mono text-xs break-all">
                {currentProjectPath}
              </span>
            </div>
          )}

          {(mode === "saveAs" || !currentProjectPath) && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Project Name
                </label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Enter project name"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isSaving || mode === "save"}
                  readOnly={mode === "save"}
                  autoFocus={mode === "saveAs"}
                />
              </div>

              {(isElectron || supportsFileSystemAccess) && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Project Location
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={projectLocation || "Not selected"}
                      readOnly
                      className={`flex-1 px-3 py-2 bg-gray-700 border rounded text-sm ${
                        projectLocation
                          ? "border-green-500 text-white"
                          : "border-gray-600 text-gray-400"
                      }`}
                      disabled
                    />
                    {mode === "saveAs" && (
                      <button
                        onClick={handleBrowse}
                        disabled={isSaving}
                        className="px-3 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors disabled:opacity-50 flex items-center gap-1"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                          />
                        </svg>
                      </button>
                    )}
                  </div>

                  <p className="text-xs text-gray-400 mt-1">
                    💡 Recommended: Documents, or a custom project folder
                  </p>
                </div>
              )}

              {projectName && projectLocation && (
                <div className="text-xs text-gray-400 bg-gray-700/50 p-2 rounded">
                  Will save to:{" "}
                  <span className="text-green-400 font-mono">
                    {projectLocation}/{projectName}/
                  </span>
                </div>
              )}
            </>
          )}

          {/* {error && (
            <div className="p-3 bg-red-900/30 border border-red-500 rounded text-red-300 text-sm">
              ⚠️ {error}
            </div>
          )} */}

          {success && (
            <div className="p-3 bg-green-900/30 border border-green-500 rounded text-green-300 text-sm">
              ✓ Project saved successfully in terminal format!
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t border-gray-700">
          <button
            onClick={handleCancel}
            disabled={isSaving}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={
              isSaving ||
              success ||
              (mode === "saveAs" && !projectName.trim()) ||
              (mode === "saveAs" &&
                (isElectron || supportsFileSystemAccess) &&
                !projectLocation)
            }
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors disabled:opacity-50 min-w-[70px]"
          >
            {isSaving ? "Saving..." : success ? "✓ Saved" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};
